import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';

import { PAGE_CONTENT_CLASS } from '../../components/layout/PageContent';
import Icon from '../../components/AppIcon';
import PageHeader from 'components/PageHeader';
import Button from '../../components/ui/Button';
import LoadingMark from '../../components/ui/LoadingMark';

import licenseService from '../../services/licenseService';

const PAGE_SIZE = 10;

const normalizePurchaseStatus = (status) => {
  const value = String(status || 'WAITING').trim().toUpperCase();
  const aliases = {
    APPROVED: 'PAID',
    PENDING: 'WAITING',
    IN_PROCESS: 'WAITING',
  };
  return aliases[value] || value;
};

const statusConfig = {
  WAITING: { label: 'Aguardando', color: 'text-yellow-600 bg-yellow-100' },
  IN_ANALYSIS: { label: 'Em Análise', color: 'text-blue-600 bg-blue-100' },
  AUTHORIZED: { label: 'Autorizado', color: 'text-indigo-600 bg-indigo-100' },
  PAID: { label: 'Pago', color: 'text-green-600 bg-green-100' },
  DECLINED: { label: 'Recusado', color: 'text-red-600 bg-red-100' },
  CANCELED: { label: 'Cancelado', color: 'text-gray-600 bg-gray-200' },
  REFUNDED: { label: 'Reembolsado', color: 'text-orange-600 bg-orange-100' },
  CHARGEBACK: { label: 'Chargeback', color: 'text-red-700 bg-red-100' },
  UNKNOWN: { label: 'Desconhecido', color: 'text-gray-600 bg-gray-200' },
};

const paymentMethodConfig = {
  PIX: { label: 'PIX', icon: 'QrCode' },
  CREDIT_CARD: { label: 'Cartão de Crédito', icon: 'CreditCard' },
  CHECKOUT_PRO: { label: 'Mercado Pago', icon: 'CreditCard' },
  SUBSCRIPTION: { label: 'Assinatura', icon: 'Calendar' },
};

const formatDate = (isoDate) => {
  if (!isoDate) return '-';
  return new Date(isoDate).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatCurrency = (value) =>
  value?.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const PurchaseHistoryScreen = () => {
  const [purchases, setPurchases] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    let pollTimer;

    const fetchPurchases = async () => {
      setLoading(true);
      setError(null);

      const response = await licenseService.getPurchases();

      if (!active) {
        return;
      }

      if (!response.success) {
        setError(response.error || 'Erro ao carregar histórico de compras');
        setLoading(false);
        return;
      }

      const items = response.data.purchaseHistory || [];
      setPurchases(items);
      setLoading(false);

      if (items.some((purchase) => normalizePurchaseStatus(purchase.status) === 'WAITING')) {
        pollTimer = window.setTimeout(fetchPurchases, 5000);
      }
    };

    fetchPurchases();
    window.addEventListener('focus', fetchPurchases);

    return () => {
      active = false;
      window.clearTimeout(pollTimer);
      window.removeEventListener('focus', fetchPurchases);
    };
  }, []);

  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank', 'width=900,height=650');

    const html = `
      <html>
        <head>
          <title>Relatório de Compras</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 24px;
              color: #111;
            }

            h1 {
              margin-bottom: 24px;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }

            th, td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }

            th {
              background: #f5f5f5;
            }

            .status-PAID { color: #15803d; font-weight: bold; }
            .status-WAITING { color: #a16207; }
            .status-IN_ANALYSIS { color: #2563eb; }
            .status-AUTHORIZED { color: #4f46e5; }
            .status-DECLINED { color: #dc2626; }
            .status-CANCELED { color: #6b7280; }
          </style>
        </head>
        <body>
          <h1>Histórico de Compras</h1>

          <table>
            <thead>
              <tr>
                <th>ID da Transação</th>
                <th>Data de Criação</th>
                <th>Última Atualização</th>
                <th>Método</th>
                <th>Status</th>
                <th>Licença</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              ${purchases.map(p => {
                const normalizedStatus = normalizePurchaseStatus(p.status);
                return `
                <tr>
                  <td>${p.order_id}</td>
                  <td>${formatDate(p.creation_date)}</td>
                  <td>${formatDate(p.updated_date)}</td>
                  <td>${p.payment_method}</td>
                  <td class="status-${normalizedStatus}">
                    ${statusConfig[normalizedStatus]?.label || normalizedStatus}
                  </td>
                  <td>${p.license}</td>
                  <td>${formatCurrency(p.amount)}</td>
                </tr>
              `;
              }).join('')}
            </tbody>
          </table>

          <script>
            window.onload = () => window.print();
          </script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <>
      <Helmet>
        <title>Histórico de Compras</title>
      </Helmet>

      <div className={PAGE_CONTENT_CLASS}>
        <PageHeader
          title="Histórico de Compras"
          subtitle="Consulte suas transações realizadas"
          icon="Receipt"
          action={
            <Button
              type="button"
              variant="outline"
              onClick={handlePrintReport}
              className="w-full sm:w-auto gap-2"
            >
              <Icon name="Printer" size={18} />
              Imprimir relatório
            </Button>
          }
        />

        {loading && (
          <div className="flex justify-center py-8">
            <LoadingMark message="Carregando histórico de compras..." />
          </div>
        )}

        {error && (
          <div className="text-center text-red-600 font-medium py-8">
            {error}
          </div>
        )}

        {!loading && !error && purchases.length === 0 && (
          <div className="text-center text-muted-foreground py-8">
            Nenhuma compra encontrada.
          </div>
        )}

        {!loading && !error && purchases.length > 0 && (
          <>
            <div className="space-y-4">
              {purchases.slice(0, visibleCount).map((purchase) => {
                const normalizedStatus = normalizePurchaseStatus(purchase.status);
                const status = statusConfig[normalizedStatus] || statusConfig.UNKNOWN;
                const paymentMethod =
                  paymentMethodConfig[purchase.payment_method] || {};

                return (
                  <div
                    key={purchase.transaction_id || purchase.order_id}
                    className="flex flex-col md:flex-row gap-4 p-4 sm:p-5 border border-border rounded-lg bg-card"
                  >
                    <div className="flex items-center gap-3 md:block">
                      <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-muted shrink-0">
                        <Icon
                          name={paymentMethod.icon || 'HelpCircle'}
                          size={24}
                        />
                      </div>
                      <div
                        className={`md:hidden px-3 py-1 rounded-full text-xs font-semibold ${status.color}`}
                      >
                        {status.label}
                      </div>
                    </div>

                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 min-w-0">
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">ID da Transação</p>
                        <p className="text-sm break-all">{purchase.order_id}</p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">Data de Criação</p>
                        <p className="text-sm">{formatDate(purchase.creation_date)}</p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">Última Atualização</p>
                        <p className="text-sm">{formatDate(purchase.updated_date)}</p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">Método de Pagamento</p>
                        <p className="text-sm">
                          {paymentMethod.label || purchase.payment_method || 'Mercado Pago'}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">Licença</p>
                        <p className="text-sm">{purchase.license}</p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">Valor</p>
                        <p className="text-sm font-medium">
                          {formatCurrency(purchase.amount)}
                        </p>
                      </div>
                    </div>

                    <div
                      className={`hidden md:block self-start md:self-center px-3 py-1 rounded-full text-xs font-semibold shrink-0 ${status.color}`}
                    >
                      {status.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {visibleCount < purchases.length && (
              <div className="flex justify-center mt-8">
                <Button
                  type="button"
                  onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
                  className="w-full sm:w-auto min-w-[140px]"
                >
                  Mostrar mais
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default PurchaseHistoryScreen;